import { useCallback, useEffect, useReducer, useRef } from 'react';
import { useStore } from '@nanostores/react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Authenticators,
  isLoginError,
  isTwoFactorAuthLinkRequiredError,
  isTwoFactorAuthRequiredError,
} from '@customerio/hydra-authentication';
import { session } from '@/services/session';
import { authenticateGoogleOneClick, authenticateWithSso, fetchSsoInfo, type SsoConfiguration } from '@/services/oauth';
import { trackEventForEnvironment } from '@/services/analytics';
import { getGaClientId } from '@/utils/ga-client-id';
import { setTwoFactorAuthLinkCookie } from '@/utils/two-factor-link-cookie';
import { isEmptyString } from '@/utils/string';
import { authKeys } from '@/services/auth/query-keys';

export type ReauthStep = 'loading' | 'password' | 'sso' | 'two_factor_code' | 'email_link_sent';

const GOOGLE_OIDC = 'google_oidc';

// The `Method` reported to the `re_authenticated` analytics event, matching the
// Ember reauth modal's values verbatim (`app/services/login.ts`).
type ReauthMethod =
  | 'password'
  | 'password-2fa'
  | 'google-one-click'
  | 'google-oidc'
  | 'okta-oidc'
  | 'azure-oidc'
  | 'generic-oidc'
  | 'generic-saml';

const SSO_METHOD_FOR_PROVIDER: Record<SsoConfiguration['identity_provider'], ReauthMethod> = {
  google_oidc: 'google-oidc',
  okta_oidc: 'okta-oidc',
  azure_oidc: 'azure-oidc',
  generic_oidc: 'generic-oidc',
  generic_saml: 'generic-saml',
};

const INVALID_PASSWORD_ERROR = 'Invalid password';
const GENERIC_LOGIN_ERROR = 'Unable to log in. Try again.';
const SSO_ERROR = "Your account couldn't be authenticated. Try again.";

const passwordErrorMessage = (error: unknown): string =>
  isLoginError(error) ? INVALID_PASSWORD_ERROR : GENERIC_LOGIN_ERROR;
const twoFactorErrorMessage = (error: unknown): string => (isLoginError(error) ? error.message : GENERIC_LOGIN_ERROR);
const ssoErrorMessage = (error: unknown): string => (error instanceof Error ? error.message : SSO_ERROR);

/** Who is re-authenticating — the layout's last-good user (see the modal props). */
export type ReauthUser = {
  id: number;
  email: string;
  /** Identity provider from the last login ('' if password). */
  externalIdentityProvider: string;
  /** The last-good account requires SSO (`account.sso_setting === 'required'`). */
  ssoRequired: boolean;
};

/** Re-auth replaces the active session slot — never adds a second login. */
function reauthMeta() {
  return { ga_client_id: getGaClientId(), add_session: false };
}

export type ReauthFlow = {
  step: ReauthStep;
  error: string | undefined;
  isSubmitting: boolean;
  password: string;
  setPassword: (value: string) => void;
  twoFactorCode: string;
  setTwoFactorCode: (value: string) => void;
  /** The SSO config once discovered, for rendering the provider-branded button. */
  ssoConfiguration: SsoConfiguration | null;
  /** True when the SSO step should show the Google button (Google IdP or one-click). */
  isGoogleReauth: boolean;
  /**
   * True for the SSO variant (Ember's `<Sso>` content). Drives the footer's Log
   * out button styling — subtle in the SSO variant, secondary otherwise — across
   * both the sign-in and 2FA sub-steps, matching Ember where the content
   * component (not the step) owns that styling.
   */
  isSsoFlow: boolean;
  /** The submit action for the current step; undefined when the step has none (email link sent). */
  submit: (() => Promise<void>) | undefined;
};

type ReauthVariant = {
  isSso: boolean;
  isGoogleOneClick: boolean;
  ssoConfiguration: SsoConfiguration | null;
  userExternalIdentityEmail: string;
  step: ReauthStep;
};

/**
 * Pick the variant exactly the way the Ember reauth modal does
 * (`reauth-modal/content/index.gts`):
 * - SSO variant when the account requires SSO **or** the user has any external
 *   identity provider — the same `ssoRequiredEnabled || externalIdentityProvider`
 *   signal, read from the last-good auth data (no `login_sso_info` call to
 *   route). Routing off `login_sso_info` instead would wrongly push a password
 *   user in an SSO-*enabled-but-not-required* account onto the SSO step.
 * - Google one-click (`externalIdentityProvider === 'google_oidc'`) → SSO step,
 *   Google button, no config fetch (Ember skips `fetchSsoInfo` for one-click).
 * - other SSO users → fetch the config for the provider-branded button and the
 *   authenticator (Ember's `<Sso>` constructor does the same). On a fetch blip
 *   the user stays on the SSO step; the submit re-fetches what it needs.
 * - everyone else → password.
 */
async function discoverReauthVariant(user: ReauthUser): Promise<ReauthVariant> {
  const { email, externalIdentityProvider, ssoRequired } = user;
  const isSso = ssoRequired || !isEmptyString(externalIdentityProvider);
  const isGoogleOneClick = externalIdentityProvider === GOOGLE_OIDC;

  if (!isSso) {
    return {
      isSso: false,
      isGoogleOneClick: false,
      ssoConfiguration: null,
      userExternalIdentityEmail: '',
      step: 'password',
    };
  }
  if (isGoogleOneClick) {
    return { isSso: true, isGoogleOneClick: true, ssoConfiguration: null, userExternalIdentityEmail: '', step: 'sso' };
  }
  try {
    const info = await fetchSsoInfo({ email, addAccount: false });
    return {
      isSso: true,
      isGoogleOneClick: false,
      ssoConfiguration: info.ssoConfiguration,
      userExternalIdentityEmail: info.userExternalIdentityEmail ?? '',
      step: 'sso',
    };
  } catch {
    return { isSso: true, isGoogleOneClick: false, ssoConfiguration: null, userExternalIdentityEmail: '', step: 'sso' };
  }
}

type ReauthState = {
  step: ReauthStep;
  isSso: boolean;
  isGoogleOneClick: boolean;
  ssoConfiguration: SsoConfiguration | null;
  userExternalIdentityEmail: string;
  password: string;
  twoFactorCode: string;
  twoFactorLoginToken: string;
  error: string | undefined;
  isSubmitting: boolean;
};

const INITIAL_STATE: ReauthState = {
  step: 'loading',
  isSso: false,
  isGoogleOneClick: false,
  ssoConfiguration: null,
  userExternalIdentityEmail: '',
  password: '',
  twoFactorCode: '',
  twoFactorLoginToken: '',
  error: undefined,
  isSubmitting: false,
};

type ReauthAction =
  | { type: 'variant_resolved'; variant: ReauthVariant }
  | { type: 'submit_started' }
  | { type: 'submit_finished' }
  | { type: 'two_factor_required'; loginToken: string }
  | { type: 'email_link_sent' }
  | { type: 'failed'; message: string }
  | { type: 'password_changed'; value: string }
  | { type: 'two_factor_code_changed'; value: string };

type ActionOfType<T extends ReauthAction['type']> = Extract<ReauthAction, { type: T }>;

// One handler per action type. The mapped type makes this exhaustive at compile
// time (a missing or extra key is a type error) — the same guarantee a
// switch/`never` gives, without the branch-per-case cyclomatic weight.
const REAUTH_HANDLERS: {
  [T in ReauthAction['type']]: (state: ReauthState, action: ActionOfType<T>) => ReauthState;
} = {
  variant_resolved: (state, action) => ({ ...state, ...action.variant }),
  submit_started: (state) => ({ ...state, error: undefined, isSubmitting: true }),
  submit_finished: (state) => ({ ...state, isSubmitting: false }),
  two_factor_required: (state, action) => ({
    ...state,
    step: 'two_factor_code',
    twoFactorLoginToken: action.loginToken,
    twoFactorCode: '',
  }),
  email_link_sent: (state) => ({ ...state, step: 'email_link_sent' }),
  failed: (state, action) => ({ ...state, error: action.message }),
  password_changed: (state, action) => ({ ...state, password: action.value }),
  two_factor_code_changed: (state, action) => ({ ...state, twoFactorCode: action.value }),
};

function reauthReducer(state: ReauthState, action: ReauthAction): ReauthState {
  // The handler for `action.type` accepts exactly `action`'s narrowed variant;
  // the indexed access widens it to the union, so re-narrow at the call.
  const handler =
    // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
    REAUTH_HANDLERS[action.type] as (state: ReauthState, action: ReauthAction) => ReauthState;
  return handler(state, action);
}

/**
 * Route an authentication failure to the next step or surface it. The 2FA
 * required/link transitions are common to every submit; the terminal error
 * copy is per-step (`mapError`), matching the Ember reauth modal.
 */
function reauthErrorAction(error: unknown, mapError: (error: unknown) => string): ReauthAction {
  if (isTwoFactorAuthRequiredError(error)) {
    return { type: 'two_factor_required', loginToken: error.twoFactorAuthLoginToken };
  }
  if (isTwoFactorAuthLinkRequiredError(error)) {
    // Persist the link token so the emailed link can authenticate — the login
    // page's `validateTwoFactorLink` reads this cookie alongside the URL token.
    // Ember's password reauth content sets the same cookie here.
    setTwoFactorAuthLinkCookie(error.twoFactorAuthLinkToken);
    return { type: 'email_link_sent' };
  }
  return { type: 'failed', message: mapError(error) };
}

/**
 * State machine behind the re-auth modal: picks the variant (password vs SSO,
 * the way the login page does), runs the authenticators with the 2FA
 * sub-steps, and re-resolves the auth query once the session is
 * re-established — by this tab or any other (`session.isAuthenticated` flips
 * on the cross-tab broadcast too).
 *
 * The modal is managed, so a fresh instance mounts per open — this hook's state
 * starts clean each time without an explicit reset.
 */
export function useReauthFlow(user: ReauthUser, environmentId: string | null): ReauthFlow {
  const queryClient = useQueryClient();
  const isAuthenticated = useStore(session.isAuthenticated);
  const [state, dispatch] = useReducer(reauthReducer, INITIAL_STATE);
  const { id: userId, email, externalIdentityProvider, ssoRequired } = user;

  // Fire the `re_authenticated` analytics event (best-effort) at submit
  // kick-off, matching Ember: it tracks on the last-good environment the moment
  // the authenticator runs, before the request resolves. Skipped when the
  // environment is unknown (nothing to scope the event to).
  const trackReauth = useCallback(
    (method: ReauthMethod) => {
      if (environmentId == null) {
        return;
      }
      void trackEventForEnvironment(environmentId, 'user', 'reAuthenticated', { Method: method });
    },
    [environmentId],
  );

  // The session was re-established (here, in another tab, or by the embedded
  // app): re-resolve the auth query so the layout clears the rejection, closes
  // this modal, and rehydrates the app in place.
  useEffect(() => {
    if (isAuthenticated) {
      void queryClient.invalidateQueries({ queryKey: authKeys.authenticated() });
    }
  }, [isAuthenticated, queryClient]);

  // Variant discovery on mount.
  useEffect(() => {
    let cancelled = false;
    void discoverReauthVariant({ id: userId, email, externalIdentityProvider, ssoRequired }).then((variant) => {
      if (!cancelled) {
        dispatch({ type: 'variant_resolved', variant });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [userId, email, externalIdentityProvider, ssoRequired]);

  // Synchronous re-entry guard (Ember's `dropTask`): a `useState` flag lags a
  // render, so two clicks landing in the same tick would both start a submit.
  // Covers the whole operation — including the SSO config re-fetch below — so
  // the button can't launch overlapping IdP popups.
  const isRunningRef = useRef(false);
  const runAuthenticate = useCallback(
    async (authenticate: () => Promise<unknown>, mapError: (error: unknown) => string) => {
      if (isRunningRef.current) {
        return;
      }
      isRunningRef.current = true;
      dispatch({ type: 'submit_started' });
      try {
        await authenticate();
        // Success flips `session.isAuthenticated`; the effect above re-resolves
        // the auth query and the layout closes the modal.
      } catch (error) {
        dispatch(reauthErrorAction(error, mapError));
      } finally {
        isRunningRef.current = false;
        dispatch({ type: 'submit_finished' });
      }
    },
    [],
  );

  const {
    step,
    isSso,
    isGoogleOneClick,
    ssoConfiguration,
    userExternalIdentityEmail,
    password,
    twoFactorCode,
    twoFactorLoginToken,
  } = state;

  // Google gets its own branded button — a one-click Google user, or a resolved
  // Google IdP config. Matches Ember's button condition (`isGoogleOneClick` OR
  // `ssoConfiguration.identityProvider === GoogleOIDC`).
  const isGoogleReauth = isGoogleOneClick || ssoConfiguration?.identity_provider === GOOGLE_OIDC;

  const submitForStep: Partial<Record<ReauthStep, () => Promise<void>>> = {
    password: async () => {
      if (isEmptyString(password)) {
        return;
      }
      await runAuthenticate(() => {
        trackReauth('password');
        return session.authenticate(Authenticators.EmailPassword, { email, password, ...reauthMeta() });
      }, passwordErrorMessage);
    },
    sso: async () => {
      // Mirror Ember's `<Sso>` loginTask: a Google one-click user
      // (`externalIdentityProvider === 'google_oidc'`) always goes through the
      // one-click authenticator; every configured IdP (Google SSO, Okta, Azure,
      // generic OIDC/SAML) goes through `authenticateWithSso`. Keying on
      // `isGoogleOneClick` — the same signal Ember uses — not on the config, so a
      // Google IdP user without an external-identity email still runs the SSO
      // flow rather than one-click.
      if (isGoogleOneClick) {
        await runAuthenticate(() => {
          trackReauth('google-one-click');
          return authenticateGoogleOneClick(email, userId, reauthMeta());
        }, ssoErrorMessage);
        return;
      }
      // The config re-fetch runs inside `runAuthenticate` so it's covered by the
      // submitting guard. Discovery couldn't reach `login_sso_info` (a fetch
      // blip left this SSO user without a config) → re-fetch now; an unavailable
      // config throws through to the SSO error message. Take the identity email
      // from the *same* re-fetch — discovery's value is stale (usually empty),
      // and it's the Google `login_hint`, so a stale one breaks the re-auth.
      await runAuthenticate(async () => {
        let config = ssoConfiguration;
        let identityEmail = userExternalIdentityEmail;
        if (config == null) {
          const info = await fetchSsoInfo({ email, addAccount: false });
          config = info.ssoConfiguration;
          identityEmail = info.userExternalIdentityEmail ?? '';
        }
        if (config == null) {
          throw new Error('SSO configuration unavailable');
        }
        trackReauth(SSO_METHOD_FOR_PROVIDER[config.identity_provider]);
        return authenticateWithSso(config, email, { userExternalIdentityEmail: identityEmail, meta: reauthMeta() });
      }, ssoErrorMessage);
    },
    two_factor_code: async () => {
      if (isEmptyString(twoFactorCode)) {
        return;
      }
      await runAuthenticate(() => {
        trackReauth('password-2fa');
        return session.authenticate(Authenticators.TwoFactor, {
          '2fa_login_token': twoFactorLoginToken,
          '2fa_code': twoFactorCode,
          ...reauthMeta(),
        });
      }, twoFactorErrorMessage);
    },
  };

  return {
    step,
    error: state.error,
    isSubmitting: state.isSubmitting,
    password,
    setPassword: (value) => {
      dispatch({ type: 'password_changed', value });
    },
    twoFactorCode,
    setTwoFactorCode: (value) => {
      dispatch({ type: 'two_factor_code_changed', value });
    },
    ssoConfiguration,
    isGoogleReauth,
    isSsoFlow: isSso,
    submit: submitForStep[step],
  };
}
